import {
  useNotificationDrawerStore,
  useNotificationsData,
} from '@notifications/entities/notification';
import { Bell } from 'lucide-react';
import { Badge, Button, Tooltip } from '@/shared/ui';
import css from './notification-drawer-trigger.module.scss';

/** Two digits keep the badge inside the 44px control; beyond that it reads "99+". */
const BADGE_MAX = 99;

type NotificationDrawerTriggerProps = {
  className?: string;
};

// Header bell. Rendered in BOTH headers (desktop top bar + mobile header), so
// it takes a className and owns no layout of its own.
//
// Only mounted for roles that participate in notifications
// (`notifications:read`) — its count query never fires for anyone else.
export const NotificationDrawerTrigger = ({
  className,
}: NotificationDrawerTriggerProps) => {
  const openDrawer = useNotificationDrawerStore((s) => s.openDrawer);
  // Shared state from `NotificationsProvider` — deliberately NOT its own query,
  // so mounting this in both headers cannot create a second polling timer.
  const { canRead, unreadCount } = useNotificationsData();

  if (!canRead) return null;

  const count = unreadCount;
  const hasUnread = count > 0;
  // The count is in the accessible name, not only in the badge, so the state is
  // announced rather than purely visual.
  const label = hasUnread
    ? `Notifications, ${count} unread`
    : 'Notifications, none unread';

  return (
    <Tooltip content="Notifications" position="bottom">
      <div className={css.root}>
        <Button
          aria-label={label}
          className={className}
          icon={<Bell className={css.icon} />}
          onClick={openDrawer}
          size="icon"
          variant="ghost"
        />
        {hasUnread ? (
          <span className={css.badgeAnchor}>
            <Badge variant="error">
              {count > BADGE_MAX ? `${BADGE_MAX}+` : count}
            </Badge>
          </span>
        ) : null}
      </div>
    </Tooltip>
  );
};
